import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { IMG } from "../data/content";
import Seo from "../components/Seo";
import SpiritCallout from "../components/SpiritCallout";

const INFORMATION_TYPES = [
  "Seed collection locations",
  "Provenance and genetics information",
  "Species occurrence records",
  "Seasonal observations",
  "Cultural plant knowledge",
  "Restoration and monitoring data",
  "GIS mapping and spatial information",
];

const GOVERNANCE_CONTROLS = [
  "What information is recorded",
  "Who can access information",
  "What information can be shared",
  "What information remains restricted",
  "How knowledge is stored for future generations",
];

const MAPPING_USES = [
  "Provenance management",
  "Seed collection planning",
  "Species distribution mapping",
  "Restoration planning",
  "Monitoring and stewardship activities",
  "Climate adaptation assessments",
];

export default function Intelligence() {
  return (
    <div data-testid="page-intelligence">
      <Seo
        title="Expert-Led Seed Intelligence"
        description="Seed Intelligence — provenance, GIS mapping and expert-led data governance for restoration. Cultural information and collection records remain under Traditional Owner control."
        path="/seed-intelligence"
      />
      {/* Hero */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="stamp">Core to SeedKeepers</span>
              <span className="specimen-label">Expert-Led Seed Intelligence</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              Every seed carries more than <em className="text-eucalypt">biological material.</em>
            </h1>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              It carries information about place, species, timing, landscape
              condition, cultural knowledge and future restoration potential.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              SeedKeepers supports expert-led approaches to seed
              intelligence — helping communities and organisations capture,
              manage and apply information that strengthens restoration
              outcomes while respecting cultural authority.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img src={IMG.labelledJars} alt="Labelled seed jars" className="w-full h-[440px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Information may include */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">Information may include</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-12 max-w-3xl leading-tight">
            What seed intelligence actually covers.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-rule border border-rule" data-testid="information-types">
            {INFORMATION_TYPES.map((f, i) => (
              <div
                key={f}
                className="bg-card p-6 md:p-7 min-h-[150px] flex flex-col justify-between"
                data-testid={`information-type-${i}`}
              >
                <span className="specimen-label">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-serif text-xl md:text-2xl leading-tight">{f}</p>
              </div>
            ))}
            <div className="bg-bark text-chalk p-6 md:p-7 min-h-[150px] flex flex-col justify-center">
              <p className="specimen-label text-chalk/60 mb-3">Held together</p>
              <p className="font-serif text-lg md:text-xl leading-snug">
                Under cultural authority, on Country.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Indigenous Data Governance */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-center gap-3 mb-4">
            <Lock size={18} className="text-ochre" />
            <span className="specimen-label text-chalk/60">Indigenous Data Governance</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-4xl mb-10">
            Information generated through seed collection and restoration should remain connected to Country and <em className="text-ochre">governed by Traditional Owners.</em>
          </h2>
          <p className="text-base md:text-lg text-chalk/80 leading-relaxed max-w-3xl mb-14">
            Where appropriate, SeedKeepers can support the development of
            secure, password-protected data systems that allow Traditional
            Owners to determine:
          </p>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-chalk/15 border border-chalk/15" data-testid="governance-controls">
            {GOVERNANCE_CONTROLS.map((d, i) => (
              <li
                key={d}
                className="bg-bark p-6 md:p-8 flex gap-5 items-start"
                data-testid={`governance-control-${i}`}
              >
                <span className="font-serif text-3xl md:text-4xl text-ochre leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-serif text-xl md:text-2xl leading-snug">{d}</p>
              </li>
            ))}
            <li className="bg-bark p-6 md:p-8 flex flex-col justify-center border-t md:border-t-0 md:border-l border-chalk/15">
              <span className="specimen-label text-ochre mb-3">Principle</span>
              <p className="font-serif text-xl md:text-2xl leading-snug text-chalk">
                Decisions about information rest with the people whose Country
                it concerns.
              </p>
            </li>
          </ul>

          <div className="mt-14 border-l-2 border-ochre pl-6 max-w-3xl">
            <p className="specimen-label text-ochre mb-3">Our undertaking</p>
            <p className="font-serif text-2xl md:text-3xl leading-snug text-chalk">
              All cultural information, collection records and associated
              datasets remain under the control of Traditional Owners and are
              managed in accordance with community protocols, permissions and
              governance arrangements.
            </p>
          </div>
        </div>
      </section>

      {/* Mapping Country */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <img
              src={IMG.truck}
              alt="Field vehicle on Country at dusk"
              className="w-full h-[460px] object-cover border border-rule p-2 bg-cream"
            />
          </div>
          <div className="lg:col-span-7">
            <p className="section-num mb-3">Mapping Country</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              GIS and spatial mapping can support better decisions about how seed is collected, stored and returned to Country.
            </h2>
            <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
              By combining local knowledge, ecological information and spatial
              tools, communities can make more informed decisions about how
              seed is collected, stored and returned to Country.
            </p>
            <ul
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-rule border border-rule"
              data-testid="mapping-uses"
            >
              {MAPPING_USES.map((m, i) => (
                <li
                  key={m}
                  className="bg-card p-5 md:p-6 flex gap-4 items-start"
                  data-testid={`mapping-use-${i}`}
                >
                  <span className="specimen-label text-eucalypt mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="font-serif text-lg md:text-xl leading-snug">{m}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SPIRIT Platform — partner CTA */}
      <SpiritCallout
        variant="condensed"
        sectionLabel="The SPIRIT Platform"
        id="spirit"
        source="intelligence"
      />

      {/* Closing */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="section-num mb-8">Our Position</p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
            SeedKeepers views data as a <em className="text-eucalypt">stewardship responsibility</em>, not an asset to be extracted.
          </h2>
          <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed max-w-3xl mx-auto">
            Our role is to help communities build secure systems that support
            restoration, protect cultural knowledge and maintain Traditional
            Owner authority over information generated on Country.
          </p>
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="intelligence-contact-cta"
              className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            >
              Talk to us about Seed Intelligence <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
